import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { verificationApi } from '../api/verificationApi';
import { ivKeys } from '../queryKeys';

/**
 * Mutations that act on a single verification. Every one invalidates the
 * detail record plus the location's worklist and metrics so the list, the
 * attention cards and the detail page all agree after an action.
 */
export function useVerificationActions(id: string) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ivKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: ivKeys.worklistPrefix(locationId) });
    queryClient.invalidateQueries({ queryKey: ivKeys.metrics(locationId) });
  }

  const approve = useMutation({
    mutationFn: () => verificationApi.approve(id),
    onSuccess: invalidate,
  });

  const flag = useMutation({
    mutationFn: (note?: string) => verificationApi.flag(id, note),
    onSuccess: invalidate,
  });

  const queueCall = useMutation({
    mutationFn: () => verificationApi.queueCall(id),
    onSuccess: invalidate,
  });

  const rerun = useMutation({
    mutationFn: () => verificationApi.rerun(id),
    onSuccess: invalidate,
  });

  // Hard delete. The detail query is removed (not refetched) so the page
  // doesn't 404 on its way back to the worklist.
  const remove = useMutation({
    mutationFn: () => verificationApi.deleteVerification(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ivKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ivKeys.worklistPrefix(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.metrics(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.batches(locationId) });
    },
  });

  return { approve, flag, queueCall, rerun, remove, locationId };
}

/**
 * Fetches one patient's full DOB. The server records every reveal on the
 * verification's audit trail, so the detail record is refreshed to show it.
 */
export function useRevealDob(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => verificationApi.revealDob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.detail(id) });
    },
  });
}
